/**
 * auth store - JWT 토큰·식별자 상태 + login/refresh/logout actions.
 * localStorage 자동 persist (pinia-plugin-persistedstate), AuthController 응답 shape 와 1:1 정합.
 */
import { defineStore } from 'pinia';
import axios from 'axios';
import { useLocaleStore } from '@/stores/locale';
import { useThemeStore } from '@/stores/theme';
import { useCodeStore } from '@/stores/code';
import { useHrStdUseStore } from '@/stores/hrStdUse';
import { DEFAULT_LOCALE } from '@/locales';
import { clearSessionCaches } from '@/services/sessionCache';

const RAW_BASE = import.meta.env.VITE_API_BASE_URL || '';

// 인증 경로는 services/http 인스턴스를 타지 않는다(interceptor 의 401 재시도와 얽히므로).
// 언어 헤더는 여기서 직접 붙여야 로그인 실패 문구가 화면 언어로 온다.
function authHeaders() {
  return { 'Content-Type': 'application/json', 'Accept-Language': useLocaleStore().locale };
}

// JWT exp 로 만료 여부 판정. 형식을 못 읽으면 false(만료 아님)로 둔다 - 판정 실패가
// 멀쩡한 세션을 끊는 쪽으로 기울면 안 되고, 그 경우는 서버 401 이 정상 경로로 처리한다.
const CLOCK_SKEW_SEC = 30;
// 갱신 대기 상한. 화면 이동을 막는 호출이라 http 기본값(30초)보다 짧게 잡는다.
const REFRESH_TIMEOUT_MS = 8000;
export function jwtExpired(token) {
  const part = String(token || '').split('.')[1];
  if (!part) return false;
  try {
    const json = decodeURIComponent(escape(atob(part.replace(/-/g, '+').replace(/_/g, '/'))));
    const exp = JSON.parse(json)?.exp;
    if (!exp) return false;
    return exp * 1000 <= Date.now() + CLOCK_SKEW_SEC * 1000;
  } catch {
    return false;
  }
}

// JWT roles 클레임(백엔드 쓰기 게이트와 같은 기준). 형식을 못 읽으면 빈 배열.
export function jwtRoles(token) {
  const part = String(token || '').split('.')[1];
  if (!part) return [];
  try {
    const json = decodeURIComponent(escape(atob(part.replace(/-/g, '+').replace(/_/g, '/'))));
    const roles = JSON.parse(json)?.roles;
    return Array.isArray(roles) ? roles : [];
  } catch {
    return [];
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: '',
    refreshToken: '',
    empId: '',
    empNo: '',     // 사번(FRM_USER.USER_ID=PHM_EMP.EMP_ID 조인). 사원-사용자만, 시스템계정(admin)은 ''.
    empNm: '',     // 성명(PHM_NAME KOR). 로그인 응답에는 없어 /api/auth/me 로 보강한다. 시스템계정은 ''.
    userId: '',
    loginId: '',
    companyCd: '',
    companyNm: '',  // 상단 표시용 회사명. 법인 미등록이면 서버가 테넌트명으로 대체해 내려준다.
    // 법인 전환. companies 는 /api/auth/me 가 내려주는 전환 가능 법인, currentCompanyCd 는 선택 법인(비면 소속 법인).
    companies: [],
    allCompanyYn: 'N',
    // 메뉴·데이터 범위를 정하는 실효 권한의 유형(ADMIN·LEADER·CUSTOM·PERSONAL). /api/auth/me 가 내려준다
    authItemType: '',
    currentCompanyCd: '',
    tokenType: 'Bearer',
    // 상단바 권한(역할) 콤보 선택값. 현재는 표시·보관까지, G5 LNB 연동 후 메뉴 필터.
    selectedAuthId: '',
  }),
  getters: {
    isLoggedIn: (s) => !!s.accessToken,
    isAuthenticated: (s) => !!s.accessToken,
    // 만료 판정은 getter 로 두지 않는다. getter 는 computed 라 accessToken 이 바뀔 때만
    // 다시 계산되고 Date.now() 는 반응형이 아니라, 탭을 열어 둔 채 시간이 흘러도 과거 값이
    // 그대로 나온다(= 만료를 영영 못 잡는다). 판정이 필요한 곳에서 jwtExpired() 를 직접 부른다.
    // 요청 헤더에 싣는 작업 대상 법인. 선택이 없으면 소속 법인.
    activeCompanyCd: (s) => s.currentCompanyCd || s.companyCd,
    canSwitchCompany: (s) => (s.companies || []).length > 1,
    // 조직장으로 들어온 사용자 - 같은 화면을 조직장용으로 보인다(조직장 관리 -> 대리인 관리). 허용 판정은 서버가 한다
    isLeaderItem: (s) => s.authItemType === 'LEADER',
  },
  actions: {
    /** POST /api/auth/login - 평문 비번 송신. 응답 shape = {success,data:{accessToken,...},error}. */
    async login({ loginId, password }) {
      const resp = await axios.post(
        `${RAW_BASE}/api/auth/login`,
        { loginId, password },
        { headers: authHeaders() },
      );
      const data = resp.data.data;
      // 2단계 인증 대상이거나 비밀번호가 만료됐으면 토큰 대신 임시 토큰만 온다 - 세션을 만들지 않고 호출부가 다음 단계를 띄운다
      if (data?.tfaRequired || data?.pwdChgRequired) return data;
      this.setSession(data);
      return data;
    },
    // 2단계 인증(공개 경로, 임시 토큰). 세션이 없는 단계라 http 인스턴스의 401 재발급 처리를 타지 않게 직접 호출한다.
    async sendTfaMail(tfaToken) {
      const resp = await axios.post(`${RAW_BASE}/api/auth/tfa/mail/send`, { tfaToken }, { headers: authHeaders() });
      return resp.data.data;
    },
    async setupTfaOtp(tfaToken) {
      const resp = await axios.post(`${RAW_BASE}/api/auth/tfa/otp/setup`, { tfaToken }, { headers: authHeaders() });
      return resp.data.data;
    },
    async verifyTfa({ tfaToken, tfaNo }) {
      const resp = await axios.post(`${RAW_BASE}/api/auth/tfa/verify`, { tfaToken, tfaNo }, { headers: authHeaders() });
      const data = resp.data.data;
      if (data?.pwdChgRequired) return data;   // 인증번호는 맞았지만 비밀번호가 만료됐거나 초기 비밀번호 - 변경을 먼저 한다
      this.setSession(data);
      return data;
    },
    // 만료 비밀번호 변경(공개 경로, 변경 전용 토큰). 성공 응답이 정식 토큰이라 여기서 세션을 만든다.
    async changeExpiredPassword({ pwdChgToken, newPassword }) {
      const resp = await axios.post(`${RAW_BASE}/api/auth/password-expired/change`, { pwdChgToken, newPassword }, { headers: authHeaders() });
      this.setSession(resp.data.data);
      return resp.data.data;
    },
    // 보호 화면 진입 전 세션이 실제로 쓸 수 있는지 확인한다. 만료면 여기서 한 번 갱신하고,
    // 갱신도 안 되면 세션을 비워 가드가 로그인으로 보내게 한다(빈 셸 렌더 + 401 연쇄 차단).
    async ensureSession() {
      if (!this.accessToken) return false;
      if (!jwtExpired(this.accessToken)) return true;
      if (!this.refreshToken) { this.clear(); return false; }
      try {
        await this.refresh();
        return !jwtExpired(this.accessToken);
      } catch (e) {
        // 서버가 응답을 준 실패(401 등)만 토큰이 죽은 것이다. 응답 없는 실패(타임아웃·회선 단절)에
        // 세션을 비우면 멀쩡한 14일 refresh token 과 언어·테마 설정까지 함께 날아간다.
        if (e?.response) this.clear();
        return false;
      }
    },
    async refresh() {
      if (!this.refreshToken) throw new Error('no refresh token');
      // 라우터 가드(ensureSession)가 이 호출을 기다린다. 상한이 없으면 서버가 늦는 동안
      // 화면 이동 자체가 멈춘 것처럼 보이므로, 넘기면 실패로 보고 로그인으로 보낸다.
      const resp = await axios.post(
        `${RAW_BASE}/api/auth/refresh`,
        { refreshToken: this.refreshToken },
        { headers: authHeaders(), timeout: REFRESH_TIMEOUT_MS },
      );
      // 토큰 누락 응답에 조용히 로그아웃되지 않도록 throw (interceptor refresh 와 정합)
      const t = resp.data?.data?.accessToken;
      if (!t) throw new Error('refresh returned no accessToken');
      this.setAccessToken(t);
      return resp.data.data;
    },
    async logout() {
      // 서버 logout 은 best-effort - 실패해도 클라이언트 상태는 정리한다.
      try {
        await axios.post(`${RAW_BASE}/api/auth/logout`, {}, { headers: authHeaders() });
      } catch {
        // 백엔드 호출 실패해도 클라이언트 상태는 정리
      }
      this.clear();
    },
    setSession(payload) {
      // 새 로그인 - 앞 세션이 clear 없이 끝났어도(재발급 실패) 이전 사용자의 기준 사용 설정을 쓰지 않게 비운다
      useHrStdUseStore().invalidate();
      // 앞 세션의 실효 권한 유형도 비운다 - 내 정보가 다시 채우고, 조직장 분기 화면은 그 응답(사용 설정)을 받은 뒤 그린다
      this.authItemType = '';
      this.accessToken = payload.accessToken || '';
      this.refreshToken = payload.refreshToken || this.refreshToken;
      this.empId = payload.empId || '';
      this.empNo = payload.empNo || '';
      // 로그인/재발급 응답에 성명이 없다(TokenResponse 미포함) - 기존 보강값을 지우지 않는다.
      this.empNm = payload.empNm || this.empNm;
      this.userId = payload.userId || '';
      this.loginId = payload.loginId || '';
      this.companyCd = payload.companyCd || '';
      // 새 로그인은 소속 법인에서 시작한다(재발급은 setSession 을 타지 않아 선택이 유지된다).
      if (payload.companyCd) this.currentCompanyCd = payload.companyCd;
      // 재발급 응답에도 실려 오지만, 빠진 경우 기존 값을 지우지 않는다.
      this.companyNm = payload.companyNm || this.companyNm;
      this.tokenType = payload.tokenType || 'Bearer';
    },
    setAccessToken(token) {
      this.accessToken = token || '';
    },
    // /api/auth/me 의 전환 가능 법인 반영. 선택 법인이 목록에 없으면(권한 회수·법인 폐지) 소속 법인으로 되돌린다.
    setCompanies(list, allCompanyYn) {
      this.companies = Array.isArray(list) ? list : [];
      this.allCompanyYn = allCompanyYn === 'Y' ? 'Y' : 'N';
      if (this.currentCompanyCd && !this.companies.some((c) => c.companyCd === this.currentCompanyCd)) {
        this.currentCompanyCd = this.companyCd;
      }
    },
    // 법인 정보 저장 직후 반영. 회사명은 로그인 때 받아 둔 값이라 다음 me 조회까지 옛 이름이 남는다
    renameCompany(companyCd, companyNm) {
      const nm = String(companyNm || '').trim();
      if (!companyCd || !nm) return;
      if (companyCd === this.activeCompanyCd) this.companyNm = nm;
      this.companies = this.companies.map((c) => (c.companyCd === companyCd ? { ...c, companyNm: nm } : c));
    },
    // 선택 법인이 거부됐을 때(http 인터셉터) 소속 법인으로 복귀. 회사명은 다음 me 응답이 채운다.
    resetCompany() {
      this.currentCompanyCd = '';
      this.companyNm = '';
      // 기준 사용 설정도 법인 종속. 값만 비운다 - 거부된 법인의 응답은 오지 않고, 진행 중인 내 정보 조회는 소속 법인으로 다시 보내져 맞는 값을 채운다
      useHrStdUseStore().set(null);
    },
    // 법인 전환. 열린 화면·코드 캐시가 전부 법인 종속이라 부분 갱신 대신 전체 리로드로 새 컨텍스트에서 다시 시작한다.
    switchCompany(companyCd) {
      if (!companyCd || companyCd === this.activeCompanyCd) return;
      this.currentCompanyCd = companyCd;
      this.companyNm = '';   // 리로드 후 me 가 새 법인명을 채운다
      window.location.reload();
    },
    clear() {
      this.$reset();
      // 같은 기기에서 다른 사용자가 로그인할 때 이전 사용자의 언어·테마가 남지 않게 한다.
      useLocaleStore().setLocale(DEFAULT_LOCALE).catch(() => { /* 리소스 로드 실패는 로그아웃을 막지 않는다 */ });
      useThemeStore().resetTheme();
      // 공통코드는 법인 종속 - 다른 법인 사용자가 같은 화면에서 다시 로그인할 때 이전 코드가 남지 않게 비운다
      useCodeStore().invalidate();
      // 인사 기준 사용 설정도 법인 종속
      useHrStdUseStore().invalidate();
      // 구성원 목록 등 모듈 캐시 - 다음 사용자에게 앞 사용자의 조회 범위 목록이 남지 않게(D-213)
      clearSessionCaches();
      // 보안 - $reset 만으로는 persist 저장소가 비지 않아 로그아웃 후 reload 시 토큰이 남았다.
      // persist key 를 직접 제거해 차단한다.
      try {
        localStorage.removeItem('insait.auth');
      } catch {
        /* localStorage 접근 불가 환경(SSR·프라이빗) 무시 */
      }
    },
  },
  persist: {
    storage: localStorage,
    key: 'insait.auth',
    pick: ['accessToken', 'refreshToken', 'empId', 'empNo', 'empNm', 'userId', 'loginId', 'companyCd', 'companyNm', 'companies', 'allCompanyYn', 'authItemType', 'currentCompanyCd', 'tokenType', 'selectedAuthId'],
  },
});
